import { Component, effect, ElementRef, forwardRef, input, OnDestroy, ViewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

export interface AutocompleteOption {
  value: unknown;
  label: string;
  metaData?: unknown;
}

@Component({
  selector: 'casa-doc-input-autocomplete',
  templateUrl: './input-autocomplete.component.html',
  styleUrls: ['./input-autocomplete.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputAutocompleteComponent),
      multi: true,
    },
  ],
})
export class InputAutocompleteComponent implements ControlValueAccessor, OnDestroy {
  readonly label = input('');
  readonly placeholder = input('');
  readonly required = input(false);
  readonly hint = input('');
  readonly multiple = input(false);
  readonly options = input<AutocompleteOption[]>([]);
  readonly searchFn = input<(searchTerm: string) => Observable<AutocompleteOption[]>>();

  @ViewChild('inputRef') inputRef: ElementRef<HTMLInputElement>;

  inputControl = new FormControl<string>('');
  filteredOptions$: Observable<AutocompleteOption[]>;

  selectedOptions: AutocompleteOption[] = [];
  selectedOption: AutocompleteOption | null = null;

  private loadedOptions: AutocompleteOption[] = [];
  private lastWrittenValue: unknown | unknown[] | null = null;
  private destroy$ = new Subject<void>();
  private onChange: (value: unknown | unknown[] | null) => void = () => {};
  private onTouched: () => void = () => {};
  protected isDisabled = false;

  constructor() {
    const searchFn$ = toObservable(this.searchFn);
    const options$ = toObservable(this.options);

    this.filteredOptions$ = searchFn$.pipe(
      takeUntil(this.destroy$),
      switchMap((searchFn) => {
        if (searchFn) {
          return this.inputControl.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            startWith(''),
            switchMap((inputValue) =>
              searchFn(inputValue ?? '').pipe(
                tap((fetched) => {
                  this.loadedOptions = fetched;
                }),
                catchError(() => of([]))
              )
            )
          );
        }

        return combineLatest([this.inputControl.valueChanges.pipe(startWith('')), options$]).pipe(
          map(([inputValue, options]) => this.filterOptions(inputValue ?? '', options))
        );
      })
    );

    effect(() => {
      if (this.options().length > 0) {
        this.resolveValue(this.lastWrittenValue);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: unknown | unknown[] | null): void {
    this.lastWrittenValue = value;
    this.resolveValue(value);
  }

  registerOnChange(fn: (value: unknown | unknown[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.inputControl.disable();
    } else {
      this.inputControl.enable();
    }
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.value as unknown;
    const option = this.findOptionByValue(value);

    if (!option) {
      return;
    }

    if (this.multiple()) {
      const alreadySelected = this.selectedOptions.some((o) => o.value === value);
      if (!alreadySelected) {
        this.selectedOptions = [...this.selectedOptions, option];
        this.onChange(this.selectedOptions.map((o) => o.value));
      }
      this.inputControl.setValue('', { emitEvent: false });
      if (this.inputRef) {
        this.inputRef.nativeElement.value = '';
      }
    } else {
      this.selectedOption = option;
      this.inputControl.setValue(this.selectedOption.label, { emitEvent: false });
      this.onChange(this.selectedOption.value ?? null);
    }

    this.onTouched();
  }

  onRemoveChip(option: AutocompleteOption): void {
    this.selectedOptions = this.selectedOptions.filter((o) => o.value !== option.value);
    this.onChange(this.selectedOptions.length > 0 ? this.selectedOptions.map((o) => o.value) : null);
    this.onTouched();
  }

  onClearSingle(): void {
    this.selectedOption = null;
    this.inputControl.setValue('');
    this.onChange(null);
    this.onTouched();
  }

  onBlur(): void {
    if (this.multiple()) {
      this.inputControl.setValue('', { emitEvent: false });
      if (this.inputRef) {
        this.inputRef.nativeElement.value = '';
      }
    } else if (this.selectedOption) {
      this.inputControl.setValue(this.selectedOption.label, { emitEvent: false });
    } else {
      this.inputControl.setValue('', { emitEvent: false });
    }
    this.onTouched();
  }

  displayFn(option: AutocompleteOption | string): string {
    if (typeof option === 'string') {
      return option;
    }
    return option?.label ?? '';
  }

  private resolveValue(value: unknown | unknown[] | null): void {
    if (this.multiple()) {
      if (Array.isArray(value) && value.length > 0) {
        this.selectedOptions = value
          .map((v) => this.findOptionByValue(v))
          .filter((x) => x)
          .filter((opt): opt is AutocompleteOption => opt !== null);
      } else {
        this.selectedOptions = [];
      }
      this.inputControl.setValue('', { emitEvent: false });
    } else {
      if (value != null) {
        this.selectedOption = this.findOptionByValue(value) ?? null;
        this.inputControl.setValue(this.selectedOption?.label ?? '', { emitEvent: false });
      } else {
        this.selectedOption = null;
        this.inputControl.setValue('', { emitEvent: false });
      }
    }
  }

  private findOptionByValue(value: unknown): AutocompleteOption | undefined {
    return (
      this.options().find((opt) => opt.value === value) ??
      this.loadedOptions.find((opt) => opt.value === value)
    );
  }

  private filterOptions(value: string, options: AutocompleteOption[]): AutocompleteOption[] {
    const filterValue = value.toLowerCase();
    const alreadySelectedValues = new Set(this.selectedOptions.map((o) => o.value));
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(filterValue) &&
        (!this.multiple() || !alreadySelectedValues.has(option.value))
    );
  }
}
